/**
 * Home §F — "Technology in the water": the FishingIntelligence ocean-data scene, its six
 * layer switchers, the seven-day scrubber and the three capability tiles.
 *
 * Every layer's range is also written out as a definition list beside the scene,
 * so no fact here depends on WebGL. Layer labels and captions are translated;
 * the layer keys the shader reads are not.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';
import SceneFrame from '../three/SceneFrame';
import { Eyebrow, NavyCard } from './ui-kit';
import { useContent } from '../i18n/LocaleProvider';

export const LAYER_KEYS = [
  'temperature',
  'waves',
  'chlorophyll',
  'wind',
  'fish',
  'navigation',
];

export default function FishingIntelligenceSection() {
  const { HOME, UI } = useContent();
  const layers = UI.fishingApp.layers.map((entry, index) => ({ ...entry, key: LAYER_KEYS[index] }));
  const days = UI.fishingApp.days;
  const species = UI.fishingApp.species;

  const [layer, setLayer] = useState(0);
  const [day, setDay] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speciesIndex, setSpeciesIndex] = useState(0);
  const raf = useRef();

  // The play button advances the shader's time uniform on a 12-second loop.
  useEffect(() => {
    if (!playing) return undefined;
    let start = performance.now() - (day / 6) * 12000;
    const tick = (now) => {
      const t = ((now - start) % 12000) / 12000;
      setDay(t * 6);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  useEffect(() => {
    if (LAYER_KEYS[layer] !== 'fish') return undefined;
    const id = setInterval(() => setSpeciesIndex((s) => (s + 1) % species.length), 2500);
    return () => clearInterval(id);
  }, [layer, species.length]);

  const activeCaption =
    LAYER_KEYS[layer] === 'fish' ? species[speciesIndex] : layers[layer].caption;

  return (
    <section className="u-navy u-section" id="intelligent-fishing" aria-labelledby="fishing-app-title">
      <div className="u-container">
        <Eyebrow onNavy>{HOME.fishingApp.eyebrow}</Eyebrow>
        <h2 id="fishing-app-title" style={{ marginTop: 12, maxWidth: '22ch' }}>
          {HOME.fishingApp.h2}
        </h2>
        <p className="t-body-lg t-on-navy-secondary t-prose" style={{ marginTop: 16 }}>
          {HOME.fishingApp.intro}
        </p>

        <div style={{ marginTop: 48 }}>
          <SceneFrame
            load={() => import('../three/scenes/OceanDataScene')}
            poster="/posters/ocean-data.webp"
            posterAlt=""
            ariaLabel={`A tilted three-dimensional ocean-data plane over the Malacca Strait, Peninsular Malaysia and Sumatra, showing the ${LAYER_KEYS[layer]} layer with drifting vessel markers.`}
            style={{ aspectRatio: '16 / 9', width: '100%' }}
            sceneProps={{ layer, day, playing }}
          />

          <div
            className="layer-buttons"
            style={{ marginTop: 16 }}
            role="group"
            aria-label={UI.fishingApp.layersAria}
          >
            {layers.map((item, index) => (
              <button
                key={item.key}
                type="button"
                className="layer-button"
                aria-pressed={layer === index}
                onClick={() => setLayer(index)}
                data-testid={`fishing-app-layer-${item.key}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div
            style={{
              marginTop: 16,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <p className="t-mono" style={{ color: 'var(--cyan)', fontSize: 13 }} aria-live="polite">
              {layers[layer].label} — {activeCaption}
            </p>
            <p className="t-mono t-on-navy-secondary" style={{ fontSize: 12 }}>
              {UI.fishingApp.illustrative}
            </p>
          </div>

          <div style={{ marginTop: 24 }}>
            <div className="scrubber">
              <button
                type="button"
                className="layer-button"
                style={{ width: 48, flex: 'none' }}
                aria-pressed={playing}
                aria-label={playing ? UI.fishingApp.pause : UI.fishingApp.play}
                onClick={() => setPlaying((p) => !p)}
                data-testid="fishing-app-play"
              >
                {playing ? <Pause size={16} aria-hidden="true" /> : <Play size={16} aria-hidden="true" />}
              </button>
              <input
                type="range"
                min={0}
                max={6}
                step={0.01}
                value={day}
                onChange={(e) => {
                  setPlaying(false);
                  setDay(Number(e.target.value));
                }}
                aria-label={UI.fishingApp.timeline}
                aria-valuetext={days[Math.round(day)]}
                data-testid="fishing-app-scrubber"
              />
            </div>
            <div className="scrubber__days" aria-hidden="true">
              {days.map((d, i) => (
                <span key={d} style={{ color: Math.round(day) === i ? 'var(--cyan)' : undefined }}>
                  {d}
                </span>
              ))}
            </div>
          </div>

          {/* Text equivalent of the scene — no fact depends on WebGL. */}
          <dl
            style={{
              marginTop: 32,
              display: 'grid',
              gap: 12,
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            }}
          >
            {layers.map((item) => (
              <div key={item.key} style={{ borderTop: '1px solid var(--border-on-navy)', paddingTop: 10 }}>
                <dt className="t-micro" style={{ color: 'var(--text-link-on-navy)' }}>
                  {item.label}
                </dt>
                <dd className="t-small t-on-navy-secondary" style={{ margin: '4px 0 0' }}>
                  <span className="t-mono">{item.caption}</span> · {item.detail}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="tile-grid tile-grid--3" style={{ marginTop: 48 }}>
          {HOME.fishingApp.tiles.map((tile) => (
            <NavyCard key={tile.title}>
              <h3 style={{ fontSize: 19 }}>{tile.title}</h3>
              <p className="t-small t-on-navy-secondary" style={{ marginTop: 8 }}>
                {tile.copy}
              </p>
            </NavyCard>
          ))}
        </div>

        <p className="t-body-lg t-on-navy-secondary t-prose" style={{ marginTop: 32 }}>
          {HOME.fishingApp.closing}
        </p>
      </div>
    </section>
  );
}
